import { web3 } from "@project-serum/anchor";
import {
	IHolders,
	INewTokens,
	ITokenAccount,
	ITokenBasic,
	ITradeOrders,
	getAccountAmount,
	getMintImageURL,
	IAutomations,
	IAutomation,
	ESignalCategories,
	EActionCategories,
	ISignal,
	IAction,
	ITraderOrders,
	getSupplyNumber,
	IToken,
	TOKEN22_PROGRAM_ID, SOL_MINT, isStableCoin,
} from ".";

export function HoldersDTO(primitives: any[]): IHolders {
	const holders: IHolders = [];

	if (!primitives) return holders;

	for (const primitive of primitives) {
		holders.push({
			mint: primitive.mint,
			currentBalance: primitive.currentBalance,
			buyAmount: primitive.buyAmount,
			sellAmount: primitive.sellAmount,
			sellValue: primitive.sellValue,
			trades: primitive.trades,
			wallet: primitive.wallet,
			buyValue: primitive.buyValue,
			realisedValue: primitive.realisedValue,
			timestamp: primitive.timestamp,
		});
	}

	return holders;
}

export function TradeOrdersDTO(primitive: any): ITradeOrders {
	const orders: ITradeOrders = [];

	if (!primitive) return orders;

	for (const order of primitive) {
		orders.push({
			id: order.id,
			createdAt: new Date(order.createdAt),
			updatedAt: new Date(order.updatedAt),
			method: order.method,
			amount: order.amount,
			amountPct: order.amountPct,
			delta: order.delta,
			triggerPrice: order.highPrice,
			value: order.value,
		});
	}

	return orders;
}

export function TraderOrdersDTO(primitive: any): ITraderOrders {
	const orders: ITraderOrders = [];

	if (!primitive || primitive.error) return orders;

	for (const order of primitive) {
		orders.push({
			id: order.id,
			status: order.status,
			method: order.method,
			amount: order.amount,
			value: order.value,
			mint: order.mint,
			source: order.source,
			amountPct: order.amountPct,
		});
	}

	return orders;
}

export function TransactionsDTO(primitives, mint, decimals) {
	const txns = [];

	if (!primitives) return txns;

	for (const tx of primitives) {
		let price = 0;
		if (tx.inMint === mint) {
			price = Number(tx.value) / Number(tx.inAmount / Math.pow(10, decimals));
		} else {
			price = Number(tx.value) / Number(tx.outAmount / Math.pow(10, decimals));
		}

		txns.push({
			amount: Number(tx.inAmount),
			inMint: tx.inMint,
			outMint: tx.outMint,
			platform: tx.program,
			program: tx.program,
			sell: tx.inMint === mint,
			signer: tx.signer,
			timestamp: tx.createdAt
				? new Date(tx.createdAt).getTime()
				: new Date().getTime(),
			value: tx.value,
			price: price,
		});
	}

	return txns;
}

export function TradesDTO(primitives) {
	const txns = [];

	if (!primitives) return txns;

	for (const tx of primitives) {
		let price = 0;
		if (isStableCoin(tx.trade.inMint)) {
			price = tx.outPrice;
		} else {
			price = tx.inPrice;
		}

		txns.push({
			amount: Number(tx.trade.inAmount),
			inMint: tx.trade.inMint,
			outMint: tx.trade.outMint,
			platform: tx.platform,
			program: tx.platform,
			sell: isStableCoin(tx.trade.outMint),
			signer: tx.trade.signer,
			timestamp: tx.trade.createdAt
				? new Date(tx.trade.createdAt).getTime()
				: new Date().getTime(),
			value: tx.trade.value,
			price: price,
		});
	}

	return txns;
}

export function TokenAccountDTO(primitive: any): ITokenAccount {
	const mint = primitive.account.data.parsed.info.mint;
	return {
		primitive,
		mint: mint,
		address: new web3.PublicKey(mint),
		amount: getAccountAmount(primitive),
		decimals: primitive.account.data.parsed.info.tokenAmount.decimals,
	};
}

export function TokenBasicDTO(primitive: any): ITokenBasic {
	return {
		mint: primitive.mint,
		address: new web3.PublicKey(primitive.mint),
		decimals: primitive.decimals,
		symbol: primitive.symbol,
		name: primitive.name,
		amount: 0,
		image: primitive.imageUri.trim()
			? primitive.imageUri
			: getMintImageURL(primitive.mint),
	};
}

export function TokenDTO(primitive: any): IToken {
	return {
		address: new web3.PublicKey(primitive.mint),
		amount: 0,
		decimals: primitive?.decimals,
		extensions: [],
		mint: primitive.mint,
		name: primitive?.name,
		programID: primitive.program
			? new web3.PublicKey(primitive.program)
			: TOKEN22_PROGRAM_ID,
		supply: getSupplyNumber(primitive.supply ?? 0),
		symbol: primitive?.symbol,
		image:
			primitive.imageUri.trim() !== ""
				? primitive.imageUri
				: getMintImageURL(primitive.mint),
	};
}

export function NewTokensDTO(primitives: any[]): INewTokens {
	const tokens: INewTokens = [];

	if (!primitives) return tokens;

	for (const primitive of primitives) {
		tokens.push({
			mint: primitive.mint,
			address: new web3.PublicKey(primitive.mint),
			supply: primitive.supply,
			decimals: primitive.decimals,
			program: new web3.PublicKey(primitive.program),
			image: getMintImageURL(primitive.mint),
		});
	}

	return tokens;
}

export function AutomationsDTO(primitives: any[]): IAutomations {
	const automations: IAutomations = [];

	if (!primitives || !primitives.length) {
		return automations;
	}

	for (const primitive of primitives) {
		automations.push(AutomationDTO(primitive));
	}

	return automations;
}

export function AutomationDTO(primitive: any): IAutomation {
	const actionCategoriesMap = {
		buy: EActionCategories.TRADE,
		sell: EActionCategories.TRADE,
		swap: EActionCategories.TRADE,
		lo_create: EActionCategories.TRADE,
	};
	return {
		...primitive,
		strategy: {
			name: primitive.strategy.name,
			signals: primitive.strategy.signals.map(
				(el: any): ISignal => ({
					...el,
					category: el.type.split("_")[0],
				})
			),
			actions: primitive.strategy.actions.map(
				(el: any): IAction => ({
					...el,
					category: actionCategoriesMap[el.type],
					data: {
						...el.data,
						mint: el.data.src_mint,
					},
				})
			),
		},
	};
}
